import React, { FC, ReactElement, ReactText } from 'react';
import _isUndefined from 'lodash/isUndefined';
import _isString from 'lodash/isString';
import _isNumber from 'lodash/isNumber';
import styled from 'styled-components';
import { Theme } from '@minecraft.themes';

type LabelProps = {
  disabled: boolean;
  $isBold: boolean;
  theme: Theme;
};

/* Customize the label (the container) */
const Label = styled.label<LabelProps>`
  display: flex;
  position: relative;
  padding-left: 28px;
  margin-bottom: 10px;
  font-size: 14px;
  user-select: none;
  line-height: 20px; /* line height should be match with Checkmark height */
  min-height: 1.25rem;
  align-items: flex-end;
  ${(props) => (props.$isBold ? 'font-weight: bold;' : '')}
  transition: color 0.3s ease;
  color: ${(props): string =>
    props.disabled ? props.theme.checkbox.state.disabled.labelColor : props.theme.checkbox.labelColor};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};

  /* Hide the browser's default checkbox */
  input {
    position: absolute;
    opacity: 0;
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    height: 0;
    width: 0;
  }

  /* When the checkbox is checked/disabled, add a new background */
  input ~ span {
    background-color: ${(props) => props.theme.checkbox.backgroundColor};
    border: 1px solid
      ${(props) =>
        props.disabled ? props.theme.checkbox.state.disabled.borderColor : props.theme.checkbox.borderColor};
  }
  input:checked ~ span {
    background-color: ${(props) =>
      props.disabled ? props.theme.checkbox.state.disabled.borderColor : props.theme.checkbox.borderColor};
    border: 1px solid
      ${(props) =>
        props.disabled ? props.theme.checkbox.state.disabled.borderColor : props.theme.checkbox.borderColor};
  }

  /* Show the checkmark when checked */
  input:checked ~ span:after {
    display: block;
    left: 5px;
    top: 2px;
    width: 6px;
    height: 11px;
    border: solid white;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
  }

  /* Style the checkmark/indicator */
  span:after {
    left: 5px;
    top: 2px;
    width: 6px;
    height: 11px;
    border: solid white;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
  }
`;

const MinusableLabel = styled(Label)`
  input:not(:checked) ~ span:after {
    display: block;
    left: 3.5px;
    top: 6.5px;
    width: 9px;
    height: 11px;
    border: solid ${(props) => props.theme.checkbox.checkmarkColor};
    border-width: 3px 0 0 0;
    transform: rotate(0);
  }

  input ~ span {
    background: gray;
  }
`;

/* Create a custom checkbox */
const Checkmark = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  height: 16px;
  width: 16px;

  border-radius: 2px;
  box-sizing: content-box;
  transition: background-color 0.3s ease, border-color 0.3s ease;

  /* Create the checkmark/indicator (hidden when not checked) */
  &:after {
    content: '';
    position: absolute;
    display: none;
  }
`;

export type Props = {
  value?: any;
  label?: ReactText | ReactElement;
  name?: string;
  count?: number;
  children?: any;
  isChecked: boolean;
  isBold?: boolean;
  disabled?: boolean;
  onChange: (e?: any) => void;
  minusEnable?: boolean;
  'data-qa-id'?: string;
  className?: string;
  readOnly?: boolean;
};

const MinusableCheckbox: FC<Props> = ({
  label: primaryLabel,
  count,
  disabled: primaryDisabled,
  isChecked,
  onChange,
  children,
  minusEnable,
  isBold = false,
  className,
  ...props
}) => {
  const SelectedLabel = minusEnable ? MinusableLabel : Label;
  const withCount = !_isUndefined(count);

  const renderLabel = (): ReactElement | ReactText => {
    if (_isString(primaryLabel) || _isNumber(primaryLabel)) {
      return `${primaryLabel}${withCount ? ` (${count})` : ''}`;
    }

    return primaryLabel;
  };

  const disabled = primaryDisabled || (withCount && count <= 0);

  return (
    <SelectedLabel
      $isBold={isBold}
      disabled={disabled}
      data-qa-id={props['data-qa-id'] ? `${props['data-qa-id']}-label` : ''}
      className={className}
      data-testid="minusable-checkbox-label"
    >
      {children || renderLabel()}
      <input type="checkbox" checked={isChecked} disabled={disabled} onChange={onChange} {...props} />
      <Checkmark className="checkmark" />
    </SelectedLabel>
  );
};

export default MinusableCheckbox;
